import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, Loader2, ShieldCheck, Link2, Copy, KeyRound, LayoutGrid, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TAB_LABELS = [
  ["crm", "CRM & Statistiques"], ["manuelle", "Inscription manuelle"], ["formulaire", "Formulaire papier"],
  ["inscriptions", "Inscriptions"], ["detections", "Détections"], ["rdv", "Rendez-vous"],
  ["equipes", "Équipes"], ["logistique", "Logistique"], ["joueurs", "Joueurs"], ["staff", "Staff"],
  ["actualites", "Actualités"], ["affiliation", "Affiliation"], ["emplois", "Offres d'emploi"],
  ["evenement", "Événement"], ["contenu", "Contenu"], ["reglages", "Réglages"],
];

const ROLE_LABELS = {
  admin: "Admin — accès complet",
  secretaire: "Secrétaire — RDV, inscriptions, CRM…",
  membre: "Membre — accès limité",
};

const ROLE_DEFAULT_TABS = {
  membre: ["crm", "manuelle", "formulaire", "actualites"],
  secretaire: ["crm", "rdv", "inscriptions", "manuelle", "formulaire", "actualites"],
};

function formatApiErrorDetail(detail) {
  if (detail == null) return "Une erreur est survenue.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  return String(detail);
}

export default function AdminAccess({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ nom: "", email: "", password: "", role: "membre" });
  const [loading, setLoading] = useState(false);
  const [magicLink, setMagicLink] = useState("");
  const [generating, setGenerating] = useState(null);
  const [editing, setEditing] = useState(null); // {id, nom, role, tabs, password}
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/admin/users`, { withCredentials: true }).then((r) => setUsers(r.data)).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/admin/users`, form, { withCredentials: true });
      toast.success(`Compte ${ROLE_LABELS[form.role]?.split(" — ")[0]} créé`);
      setForm({ nom: "", email: "", password: "", role: "membre" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Supprimer l'accès de ${u.nom || u.email} ?`)) return;
    try {
      await axios.delete(`${API}/admin/users/${u.id}`, { withCredentials: true });
      toast.success("Compte supprimé");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const generateMagicLink = async (userId) => {
    setGenerating(userId || "self");
    try {
      const { data } = await axios.post(`${API}/admin/magic-links`, userId ? { user_id: userId } : {}, { withCredentials: true });
      setMagicLink(`${window.location.origin}/admin?token=${data.token}`);
      toast.success(`Lien généré pour ${data.email}`, { description: "Valable 72 h, réutilisable plusieurs fois." });
    } catch {
      toast.error("Erreur de génération du lien");
    } finally {
      setGenerating(null);
    }
  };

  const copyMagicLink = async () => {
    try {
      await navigator.clipboard.writeText(magicLink);
      toast.success("Lien copié");
    } catch {
      toast.error("Copie impossible — sélectionnez le lien manuellement");
    }
  };

  const startEdit = (u) => {
    setEditing({
      id: u.id, nom: u.nom || "", role: u.role,
      tabs: u.tabs || ROLE_DEFAULT_TABS[u.role] || [],
      customTabs: !!u.tabs,
      password: "",
    });
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const payload = { nom: editing.nom, role: editing.role, tabs: editing.customTabs ? editing.tabs : null };
      if (editing.password) payload.password = editing.password;
      await axios.put(`${API}/admin/users/${editing.id}`, payload, { withCredentials: true });
      toast.success("Compte mis à jour");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggleTab = (tabId) => {
    setEditing((e) => ({
      ...e,
      tabs: e.tabs.includes(tabId) ? e.tabs.filter((t) => t !== tabId) : [...e.tabs, tabId],
    }));
  };

  const roleBadge = (role) => role === "admin"
    ? "border-[#C5A059]/50 text-[#C5A059]"
    : role === "secretaire"
      ? "border-violet-500/50 text-violet-400"
      : "border-sky-500/50 text-sky-400";

  return (
    <div data-testid="admin-access" className="grid lg:grid-cols-2 gap-10">
      <div>
        <p className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-6">Comptes administrateurs</p>
        <div className="border border-[#27272A] divide-y divide-[#27272A]">
          {users.map((u) => (
            <div key={u.id} data-testid={`admin-user-${u.id.slice(0, 8)}`} className="px-5 py-4">
              <div className="flex items-center gap-4">
                <ShieldCheck size={16} className="text-[#C5A059] shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium">{u.nom || "Admin"} {u.id === currentUser.id && <span className="text-[#C5A059] text-xs">(vous)</span>}</p>
                  <p className="text-xs text-[#71717A]">{u.email}</p>
                  {u.tabs && <p className="text-[10px] text-[#C5A059] mt-1">Onglets personnalisés : {u.tabs.length}</p>}
                </div>
                <span className={`text-[9px] uppercase tracking-widest px-2 py-1 border shrink-0 ${roleBadge(u.role)}`}>
                  {ROLE_LABELS[u.role]?.split(" — ")[0] || u.role}
                </span>
                {u.id !== currentUser.id && (
                  <div className="flex gap-2 shrink-0">
                    <button onClick={() => generateMagicLink(u.id)} disabled={generating === u.id} data-testid={`admin-user-link-${u.id.slice(0, 8)}`} className="text-[#71717A] hover:text-[#C5A059] transition-colors" aria-label="Générer un lien de connexion" title="Lien de connexion (72 h)">
                      {generating === u.id ? <Loader2 size={15} className="animate-spin" /> : <Link2 size={15} />}
                    </button>
                    <button onClick={() => startEdit(u)} data-testid={`admin-user-edit-${u.id.slice(0, 8)}`} className="text-[#71717A] hover:text-[#C5A059] transition-colors" aria-label="Modifier les accès" title="Modifier les accès">
                      <LayoutGrid size={15} />
                    </button>
                    <button onClick={() => remove(u)} data-testid={`admin-user-delete-${u.id.slice(0, 8)}`} className="text-[#71717A] hover:text-red-400 transition-colors" aria-label="Supprimer">
                      <Trash2 size={15} />
                    </button>
                  </div>
                )}
              </div>

              {editing?.id === u.id && (
                <div className="mt-4 border border-[#C5A059]/40 bg-[#0A0A0C] p-5" data-testid="user-access-editor">
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-[10px] uppercase tracking-[0.25em] text-[#C5A059]">Accès de {u.nom || u.email}</p>
                    <button onClick={() => setEditing(null)} className="text-[#71717A] hover:text-[#C5A059]" data-testid="user-access-close"><X size={15} /></button>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4 mb-4">
                    <input value={editing.nom} onChange={(e) => setEditing({ ...editing, nom: e.target.value })} placeholder="Nom" data-testid="user-edit-nom"
                      className="bg-[#121214] border border-[#27272A] px-3 py-2.5 text-sm focus:outline-none focus:border-[#C5A059]" />
                    <select value={editing.role} onChange={(e) => setEditing({ ...editing, role: e.target.value, customTabs: false, tabs: ROLE_DEFAULT_TABS[e.target.value] || [] })} data-testid="user-edit-role"
                      className="bg-[#121214] border border-[#27272A] px-3 py-2.5 text-sm focus:outline-none focus:border-[#C5A059]">
                      {Object.entries(ROLE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </div>
                  {editing.role !== "admin" && (
                    <div className="mb-4">
                      <label className="flex items-center gap-2 text-xs text-[#A1A1AA] cursor-pointer mb-3">
                        <input type="checkbox" checked={editing.customTabs} onChange={(e) => setEditing({ ...editing, customTabs: e.target.checked, tabs: ROLE_DEFAULT_TABS[editing.role] || [] })} data-testid="user-custom-tabs" className="accent-[#C5A059]" />
                        Personnaliser les onglets (sinon : onglets par défaut du rôle)
                      </label>
                      {editing.customTabs && (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {TAB_LABELS.map(([id, label]) => (
                            <label key={id} className={`flex items-center gap-2 text-xs px-2.5 py-2 border cursor-pointer transition-colors ${editing.tabs.includes(id) ? "border-[#C5A059]/60 text-white bg-[#C5A059]/5" : "border-[#27272A] text-[#71717A]"}`} data-testid={`user-tab-${id}`}>
                              <input type="checkbox" checked={editing.tabs.includes(id)} onChange={() => toggleTab(id)} className="accent-[#C5A059]" />
                              {label}
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  <input type="password" value={editing.password} onChange={(e) => setEditing({ ...editing, password: e.target.value })} placeholder="Nouveau mot de passe (laisser vide = inchangé)" data-testid="user-edit-password"
                    className="w-full bg-[#121214] border border-[#27272A] px-3 py-2.5 text-sm focus:outline-none focus:border-[#C5A059] mb-4" />
                  <button onClick={saveEdit} disabled={saving} data-testid="user-access-save"
                    className="flex items-center gap-2 bg-[#C5A059] text-black px-6 py-2.5 text-[10px] uppercase tracking-[0.2em] font-semibold hover:bg-[#E5C07B] transition-colors disabled:opacity-60">
                    {saving && <Loader2 size={13} className="animate-spin" />} Enregistrer les accès
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="border border-[#C5A059]/40 bg-[#121214] p-6 mt-8" data-testid="magic-link-section">
          <p className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-3">Lien d'accès direct</p>
          <p className="text-xs text-[#A1A1AA] leading-relaxed mb-5">
            Générez un lien qui connecte à l'admin sans mot de passe — pour vous (bouton ci-dessous) ou pour un autre compte (icône lien à côté de son nom). Valable 72 h, réutilisable plusieurs fois (pratique sur Safari, Chrome, téléphone et ordinateur). Ne le partagez qu'avec la personne concernée.
          </p>
          <button
            type="button"
            onClick={() => generateMagicLink(null)}
            disabled={generating === "self"}
            data-testid="magic-link-generate"
            className="inline-flex items-center gap-3 border border-[#C5A059] text-[#C5A059] px-6 py-3 text-xs uppercase tracking-[0.25em] hover:bg-[#C5A059] hover:text-black transition-colors duration-300 disabled:opacity-60"
          >
            {generating === "self" ? <Loader2 size={14} className="animate-spin" /> : <Link2 size={14} />}
            Générer mon lien d'accès
          </button>
          {magicLink && (
            <div className="mt-5 flex items-center gap-3">
              <input readOnly value={magicLink} onFocus={(e) => e.target.select()} data-testid="magic-link-value"
                className="flex-1 bg-[#0A0A0C] border border-[#27272A] px-4 py-3 text-xs text-[#A1A1AA] focus:outline-none focus:border-[#C5A059] truncate" />
              <button type="button" onClick={copyMagicLink} data-testid="magic-link-copy"
                className="shrink-0 flex items-center gap-2 bg-[#C5A059] text-black px-5 py-3 text-xs uppercase tracking-[0.2em] font-semibold hover:bg-[#E5C07B] transition-colors">
                <Copy size={13} /> Copier
              </button>
            </div>
          )}
        </div>
      </div>

      <form onSubmit={submit} data-testid="admin-create-form" className="border border-[#27272A] bg-[#121214] p-8 self-start">
        <p className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-6">Créer un accès</p>
        <div className="space-y-6">
          <input data-testid="admin-create-nom" required placeholder="Nom complet *" value={form.nom} onChange={set("nom")} className="input-editorial" />
          <input data-testid="admin-create-email" required type="email" placeholder="Email *" value={form.email} onChange={set("email")} className="input-editorial" />
          <input data-testid="admin-create-password" required type="password" placeholder="Mot de passe (min. 6 caractères) *" value={form.password} onChange={set("password")} className="input-editorial" />
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#71717A]">Niveau d'accès *</label>
            <select value={form.role} onChange={set("role")} data-testid="admin-create-role" className="input-editorial mt-1">
              {Object.entries(ROLE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
        </div>
        <button type="submit" disabled={loading} data-testid="admin-create-submit" className="mt-8 inline-flex items-center gap-3 bg-[#C5A059] text-black px-8 py-3.5 text-xs uppercase tracking-[0.25em] font-semibold hover:bg-[#E5C07B] transition-colors duration-300 disabled:opacity-60">
          {loading ? <Loader2 size={15} className="animate-spin" /> : <KeyRound size={15} />}
          Créer l'accès
        </button>
      </form>
    </div>
  );
}
